import React, { useState, useMemo, useCallback } from "react";
import { Code, Loader2, DollarSign, Copy, Download } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../ui/tabs";
import { RightPanelState } from "../types";
import { FileNode } from "./FileTree";
import TerraformMermaid from "./TerraformMermaid";
import CostSummary from "./CostSummary";
import TerraformPreviewSpinner from "./TerraformPreviewSpinner";
import { Button } from "../../../ui/button";
import JSZip from "jszip";
import FileTree from "./FileTree";
import MonacoCodeViewer from "./MonacoCodeViewer";

interface TerraformFile {
  file_name?: string;
  content: string;
  type?: "terraform_file" | string;
  node?: "generate_module" | "generate_terraform" | string;
  timestamp?: string;
  request_id?: string;
  seq?: number;
  location?: string;
  module_name?: string;
  file_path?: string;
  [key: string]: any; // for extensibility
}

interface WorkspacePanelProps {
  rightPanelState: RightPanelState;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  terraformFiles: TerraformFile[] | null;
  width: number;
  costSummary?: any;
  resourceConfig?: Record<string, any>;
  isProcessing?: boolean;
}

export const WorkspacePanel: React.FC<WorkspacePanelProps> = ({
  rightPanelState,
  terraformFiles,
  width,
  costSummary,
  isProcessing,
}) => {
  // Local tab state for the workspace panel
  const [activeTab, setActiveTab] = useState("costs");
  // Track selected file by path for professional FileTree
  const [selectedPath, setSelectedPath] = useState<string | undefined>(
    undefined
  );

  // Auto-select first file when files are loaded
  React.useEffect(() => {
    if (terraformFiles?.length && !selectedPath) {
      try {
        const firstFile = terraformFiles.find(
          (f) => f.file_path || f.file_name
        );
        if (firstFile) {
          const rel = firstFile.file_path || firstFile.file_name!;
          setSelectedPath(rel);
        }
      } catch (error) {
        console.error("Error auto-selecting first file:", error);
      }
    }
  }, [terraformFiles, selectedPath]);

  // Dynamically build FileNode tree based on file_path or file_name
  const buildFileTree = useMemo(() => {
    if (!terraformFiles || terraformFiles.length === 0) return [];

    // Use a virtual root (empty name) so we don't inject extra path segments like "infra".
    const root: FileNode = {
      name: "",
      type: "folder",
      path: "",
      children: [],
    };

    terraformFiles.forEach((file) => {
      const relPath = (file.file_path || file.file_name || "").replace(
        /\\/g,
        "/"
      );
      if (!relPath) return;

      const parts = relPath.split("/");
      let current = root;

      parts.forEach((part, idx) => {
        const isLast = idx === parts.length - 1;

        if (isLast) {
          // File node
          (current.children = current.children || []).push({
            name: part,
            type: "file",
            path: relPath,
            content: file.content,
            node: file.node,
            file_path: file.file_path,
          });
        } else {
          // Folder node – reuse if existing
          if (!current.children) current.children = [];
          let next = current.children.find(
            (c) => c.type === "folder" && c.name === part
          );
          if (!next) {
            next = {
              name: part,
              type: "folder",
              path: parts.slice(0, idx + 1).join("/"),
              children: [],
            };
            current.children.push(next);
          }
          current = next;
        }
      });
    });

    // Optional: sort folders first then files alphabetically
    const sortNodes = (nodes: FileNode[]) => {
      nodes.sort((a, b) => {
        if (a.type === b.type) return a.name.localeCompare(b.name);
        return a.type === "folder" ? -1 : 1;
      });
      nodes.forEach((n) => n.children && sortNodes(n.children));
    };
    sortNodes(root.children!);
    // Return the top-level children so the tree starts directly with real folders (e.g. "environments")
    return root.children ?? [];
  }, [terraformFiles]);
  // Helper function to get file content and type from path
  const getFileFromPath = useCallback(
    (path: string): { content: string; node: string } | null => {
      if (!terraformFiles) return null;

      const pathParts = path.split("/");
      const isModulePath = pathParts.includes("modules");
      const isDocPath = pathParts.includes("docs");
      const fileName = pathParts.pop();

      let targetFileName: string;
      let expectedLocation: string;

      if (isModulePath) {
        // For module files, the path will be like "infra/modules/storage_bucket/main.tf"
        const moduleName = pathParts[pathParts.length - 1];
        targetFileName = fileName || "";
        expectedLocation = "module";
      } else if (isDocPath) {
        // For documentation files, the path will be like "infra/docs/README.md"
        targetFileName = fileName || "";
        expectedLocation = "documentation";
      } else {
        // For root files, the path will be like "infra/main.tf"
        targetFileName = fileName || "";
        expectedLocation = "root";
      }

      // 1) Try exact file_path match first (ensures correct env/module)
      const normalizedTargetPath = path; // paths now come exactly as backend sends
      let file = terraformFiles.find(
        (f) =>
          !!f.file_path &&
          f.file_path.replace(/\\/g, "/") === normalizedTargetPath
      );

      // 2) Fallback to file_name + location match (legacy behavior)
      if (!file) {
        file = terraformFiles.find(
          (f) =>
            f.file_name === targetFileName &&
            (f.location === expectedLocation ||
              (!f.location && expectedLocation === "root"))
        );
      }
      if (!file) return null;
      return {
        content: file.content || "",
        node: file.node || "terraform_file",
      };
    },
    [terraformFiles]
  );

  // Helper function to get language from filename
  const getLanguageFromFilename = (filename: string): string => {
    const extension = filename.split(".").pop()?.toLowerCase();
    switch (extension) {
      case "tf":
        return "hcl";
      case "json":
        return "json";
      case "yaml":
      case "yml":
        return "yaml";
      case "sh":
        return "bash";
      case "md":
        return "markdown";
      default:
        return "text";
    }
  };

  // Handle copy to clipboard with visual feedback
  const handleCopy = useCallback(async (content: string) => {
    if (!content) {
      console.warn("No content to copy");
      return;
    }

    try {
      await navigator.clipboard.writeText(content);
      const copyButton = document.querySelector('[aria-label="Copy code"]');
      if (copyButton instanceof HTMLElement) {
        const originalText = copyButton.innerText;
        copyButton.innerText = "Copied!";
        setTimeout(() => {
          copyButton.innerText = originalText;
        }, 2000);
      }
    } catch (err) {
      console.error("Primary copy method failed:", err);
      // Fallback for browsers that don't support clipboard API
      try {
        const textarea = document.createElement("textarea");
        textarea.value = content;
        textarea.style.position = "fixed"; // Prevent scrolling to bottom
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);

        const copyButton = document.querySelector('[aria-label="Copy code"]');
        if (copyButton instanceof HTMLElement) {
          const originalText = copyButton.innerText;
          copyButton.innerText = "Copied!";
          setTimeout(() => {
            copyButton.innerText = originalText;
          }, 2000);
        }
      } catch (fallbackErr) {
        console.error("Copy fallback failed:", fallbackErr);
        // Show user-friendly error message
        alert(
          "Failed to copy to clipboard. Please try selecting and copying manually."
        );
      }
    }
  }, []);

  // Handle download of the entire Terraform workspace as zip
  const handleDownloadCode = useCallback(() => {
    if (!terraformFiles || terraformFiles.length === 0) return;
    const zip = new JSZip();
    terraformFiles.forEach((file) => {
      if (!file.file_name) return;
      let zipPath = "";
      if (file.location === "module") {
        // Determine module name
        let moduleName = file.module_name;
        if (!moduleName && file.file_path) {
          const parts = file.file_path.replace(/\\/g, "/").split("/");
          const idx = parts.findIndex((p) => p === "modules");
          if (idx !== -1 && idx + 1 < parts.length) {
            moduleName = parts[idx + 1];
          }
        }
        if (!moduleName) {
          moduleName = file.file_name.split("/")[0];
        }
        const filenameOnly = file.file_name.includes("/")
          ? file.file_name.split("/").pop()!
          : file.file_name;
        zipPath = `infra/modules/${moduleName}/${filenameOnly}`;
      } else if (file.location === "documentation") {
        zipPath = `infra/docs/${file.file_name}`;
      } else {
        zipPath = `infra/${file.file_name}`;
      }
      zip.file(zipPath, file.content || "");
    });
    zip.generateAsync({ type: "blob" }).then((blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "caden_workspace.zip";
      link.click();
      URL.revokeObjectURL(url);
    });
  }, [terraformFiles]);

  // Helper to render Terraform diagram (React Flow or Mermaid)
  const renderTerraformDiagram = useMemo(() => {
    if (terraformFiles) {
      console.log("Available files:");
      terraformFiles.forEach((file, index) => {
        console.log(`File ${index}:`, {
          file_name: file.file_name,
          fileName: file.fileName,
          file_path: file.file_path,
          filePath: file.filePath,
          content_preview: file.content?.substring(0, 100),
        });
      });
    }

    const diagramFile = terraformFiles?.find(
      (f) =>
        f.file_name === "diagrams.md" ||
        f.fileName === "diagrams.md" ||
        f.file_path === "diagrams.md" ||
        f.filePath === "diagrams.md" ||
        (f.file_name && f.file_name.endsWith("diagrams.md")) ||
        (f.fileName && f.fileName.endsWith("diagrams.md"))
    );
    if (!diagramFile) {
      return (
        <div className="my-6 rounded-xl border border-gray-200 bg-white shadow-sm flex items-center justify-center h-[40vh] sm:h-[45vh] md:h-[50vh] lg:h-[55vh] xl:h-[60vh] max-h-[700px] text-gray-500">
          Please generate Terraform code to view the architecture diagram.
        </div>
      );
    }

    const mermaidDef = diagramFile.content;

    return (
      <div className="my-6 rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="p-4 h-[40vh] sm:h-[45vh] md:h-[50vh] lg:h-[55vh] xl:h-[60vh] max-h-[700px]">
          <TerraformMermaid definition={mermaidDef} />
        </div>
      </div>
    );
  }, [terraformFiles]);

  return (
    <div
      className="flex flex-col bg-white rounded-[20px] shadow-sm overflow-hidden border border-gray-200 h-full"
      style={{
        width: `${width}%`,
        minWidth: "400px",
      }}
    >
      <div className="border-b px-6 py-4 flex justify-between items-center h-[60px] shrink-0">
        <h2 className="text-lg font-medium">Workspace</h2>
        <div className="flex items-center gap-2 relative group">
          <Button
            className="bg-green-500 hover:bg-black text-white px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-sm font-medium transition-colors duration-200 cursor-not-allowed"
            disabled
            size="sm"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="mr-1 h-4 w-4"
            >
              <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
              <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22 22 0 0 1-4 2z" />
              <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
              <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
            </svg>
            Deploy
          </Button>
          <div className="absolute opacity-0 group-hover:opacity-100 transition-opacity duration-200 top-[120%] right-0 bg-white rounded-lg shadow-lg p-4 min-w-[200px] flex items-center gap-3 z-50">
            <svg
              className="text-gray-400 shrink-0"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path
                d="M12 16V12M12 8H12.01"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <span className="text-sm text-gray-600 font-[-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Oxygen,Ubuntu,Cantarell,'Open Sans','Helvetica Neue',sans-serif]">
              Available for premium users
            </span>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col">
        {rightPanelState === "empty" && !isProcessing && (
          <div className="h-full flex flex-col items-center justify-center text-gray-400 space-y-4">
            <p>Caden is ready to help you build infrastructure</p>
          </div>
        )}

        {(rightPanelState === "loading" || isProcessing) && (
          <div className="h-full flex items-center justify-center">
            <TerraformPreviewSpinner />
          </div>
        )}

        {!isProcessing &&
          (rightPanelState === "content" ||
            rightPanelState === "terraform") && (
            <div className="p-6 flex-1 flex flex-col min-h-0">
              <Tabs
                defaultValue="costs"
                value={activeTab}
                onValueChange={setActiveTab}
                className="w-full flex-1 flex flex-col"
              >
                <div className="mb-6 shrink-0">
                  <TabsList className="inline-flex">
                    <TabsTrigger
                      value="costs"
                      className="flex items-center gap-1"
                    >
                      <DollarSign className="h-4 w-4" />
                      Costs
                    </TabsTrigger>
                    <TabsTrigger
                      value="graph"
                      className="flex items-center gap-1"
                    >
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="text-gray-400"
                      >
                        <circle
                          cx="7"
                          cy="15"
                          r="2"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          fill="#fff"
                        />
                        <circle
                          cx="10"
                          cy="7"
                          r="2"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          fill="#fff"
                        />
                        <circle
                          cx="15"
                          cy="15"
                          r="2"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          fill="#fff"
                        />
                        <path
                          d="M6.5 13L8.5 9.5M11.5 9.5L13.5 13"
                          stroke="currentColor"
                          strokeWidth="1.2"
                          strokeLinecap="round"
                        />
                      </svg>
                      Graph
                    </TabsTrigger>
                    <TabsTrigger
                      value="code"
                      className="flex items-center gap-1"
                    >
                      <Code className="h-4 w-4" />
                      Code
                    </TabsTrigger>
                  </TabsList>
                </div>

                <TabsContent value="costs" className="mt-0 flex-1">
                  {activeTab === "costs" ? (
                    costSummary ? (
                      (() => {
                        const isValidCostSummary =
                          costSummary &&
                          typeof costSummary === "object" &&
                          ("resource_costs" in costSummary ||
                            "total_monthly_cost" in costSummary);

                        if (!isValidCostSummary) {
                          console.warn(
                            "Invalid cost summary data:",
                            costSummary
                          );
                          return (
                            <div className="flex flex-col items-center justify-center h-64 space-y-4">
                              <div className="text-center text-red-500">
                                <p>
                                  Opps! Our AI is having trouble loading cost
                                  data
                                </p>
                                <p className="text-sm text-gray-400 mt-1">
                                  Please try regenerating the infrastructure.
                                </p>
                              </div>
                            </div>
                          );
                        }

                        return <CostSummary summary={costSummary} />;
                      })()
                    ) : (
                      <div className="flex flex-col items-center justify-center h-64 space-y-4">
                        <Loader2 className="h-8 w-8 text-gray-500 animate-spin" />
                        <div className="text-center text-gray-500">
                          <p>Calculating infrastructure costs...</p>
                          <p className="text-sm text-gray-400 mt-1">
                            This may take a few moments
                          </p>
                        </div>
                      </div>
                    )
                  ) : null}
                </TabsContent>

                <TabsContent value="graph" className="mt-0 flex-1">
                  {renderTerraformDiagram}
                </TabsContent>

                <TabsContent
                  value="code"
                  className="mt-0 flex-1 flex flex-col h-full"
                >
                  <div className="flex-1 flex flex-col">
                    {rightPanelState === "terraform" ||
                    (rightPanelState === "content" &&
                      terraformFiles.length > 0) ? (
                      <div className="flex flex-1 h-full overflow-hidden">
                        {/* Scrollable FileTree */}
                        <FileTree
                          files={buildFileTree}
                          onFileSelect={(path) => setSelectedPath(path)}
                          selectedPath={selectedPath}
                        />
                        {/* Monaco code viewer */}
                        {selectedPath ? (
                          (() => {
                            const fileData = getFileFromPath(selectedPath);
                            if (!fileData) return null;
                            return (
                              <div className="flex-1 min-h-0 flex flex-col">
                                {/* Header with filename and actions */}
                                <div className="flex items-center justify-between bg-gray-100 border-b px-4 py-2 shrink-0">
                                  <span className="text-sm font-medium truncate">
                                    {selectedPath}
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      aria-label="Copy code"
                                      onClick={() =>
                                        handleCopy(fileData.content)
                                      }
                                    >
                                      <Copy className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      aria-label="Save file"
                                      onClick={handleDownloadCode}
                                    >
                                      <Download className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                                {/* Code viewer */}
                                <div className="flex-1 min-h-0">
                                  <MonacoCodeViewer
                                    code={fileData.content}
                                    language={getLanguageFromFilename(
                                      selectedPath
                                    )}
                                    height="100%"
                                    type={
                                      fileData.node === "generate_module"
                                        ? "module"
                                        : "root"
                                    }
                                  />
                                </div>
                              </div>
                            );
                          })()
                        ) : (
                          <div className="flex-1 flex items-center justify-center text-gray-500">
                            Select a file to view its contents
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="border rounded-lg p-4 text-center text-gray-500 flex-1 flex items-center justify-center">
                        <p>Terraform code will appear here</p>
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
      </div>
    </div>
  );
};
